<script lang="ts" setup>
import { Button } from "@frontiers-vue3/public-pages";
import type { IJournal, ISection } from "~/types";

const { currentResearchTopic } = useMain();
const journals = currentResearchTopic?.journals || [];

const primaryJournal = computed(() => journals.find((j) => j.isPrimary));

const otherJournals = computed((): IJournal[] =>
  [...journals]
    .filter((j) => !j.isPrimary)
    .sort((a, b) => {
      a.sections?.sort((s1, s2) => s1.title.localeCompare(s2.title));
      return a.title.localeCompare(b.title);
    })
);

const hasImpactMetrics = computed(() => {
  const m = primaryJournal.value?.impactMetrics;
  return m && (m.impactFactor > 0 || m.citeScore > 0);
});

const isExpanded = ref(false);

const buttonText = computed(() =>
  isExpanded.value ? "View less" : "View more"
);

const journalHref = (journal: IJournal) =>
  journal.isOnline ? `/journals/${journal.slug}` : undefined;

const sectionHref = (journal: IJournal, section: ISection) =>
  section.isOnline
    ? `/journals/${journal.slug}/sections/${section.slug}`
    : undefined;

const primaryJournalHref = computed(() =>
  primaryJournal.value ? journalHref(primaryJournal.value) : undefined
);
</script>
<template>
  <div
    v-if="primaryJournal"
    class="CardPublishedIn">
    <p class="CardPublishedIn__label">Published in</p>

    <h3 class="CardPublishedIn__journal">
      <a
        v-if="primaryJournalHref"
        :href="primaryJournalHref"
        class="CardPublishedIn__link">
        {{ primaryJournal.title }}
      </a>
      <template v-else>{{ primaryJournal.title }}</template>
    </h3>

    <ul
      v-if="primaryJournal.sections?.length"
      class="CardPublishedIn__sections">
      <li
        v-for="section in primaryJournal.sections"
        :key="section.sectionId"
        class="CardPublishedIn__section">
        <a
          v-if="sectionHref(primaryJournal, section)"
          :href="sectionHref(primaryJournal, section)"
          class="CardPublishedIn__link">
          Sec. {{ section.title }}
        </a>
        <template v-else>Sec. {{ section.title }}</template>
      </li>
    </ul>

    <div
      v-if="hasImpactMetrics"
      class="CardPublishedIn__metrics">
      <component
        :is="primaryJournalHref ? 'a' : 'span'"
        v-if="primaryJournal.impactMetrics.impactFactor > 0"
        v-bind="primaryJournalHref ? { href: primaryJournalHref } : {}"
        class="CardPublishedIn__metric"
        :class="{ 'CardPublishedIn__link': !!primaryJournalHref }">
        <strong>{{ primaryJournal.impactMetrics.impactFactor }}</strong>
        impact factor
      </component>
      <component
        :is="primaryJournalHref ? 'a' : 'span'"
        v-if="primaryJournal.impactMetrics.citeScore > 0"
        v-bind="primaryJournalHref ? { href: primaryJournalHref } : {}"
        class="CardPublishedIn__metric"
        :class="{ 'CardPublishedIn__link': !!primaryJournalHref }">
        <strong>{{ primaryJournal.impactMetrics.citeScore }}</strong>
        citescore
      </component>
    </div>
    <div
      class="CardPublishedIn__otherJournals"
      v-if="otherJournals.length">
      <p class="CardPublishedIn__count">
        {{ otherJournals.length }} participating journal{{
          otherJournals.length > 1 ? "s" : ""
        }}
      </p>
      <ul
        v-if="isExpanded"
        class="CardPublishedIn__others">
        <li
          v-for="journal in otherJournals"
          :key="journal.id">
          <h4 class="CardPublishedIn__journal">
            <a
              v-if="journalHref(journal)"
              :href="journalHref(journal)"
              class="CardPublishedIn__link">
              {{ journal.title }}
            </a>
            <template v-else>{{ journal.title }}</template>
          </h4>
          <ul
            v-if="journal.sections?.length"
            class="CardPublishedIn__sections">
            <li
              v-for="section in journal.sections"
              :key="section.sectionId"
              class="CardPublishedIn__section">
              <a
                v-if="sectionHref(journal, section)"
                :href="sectionHref(journal, section)"
                class="CardPublishedIn__link">
                Sec. {{ section.title }}
              </a>
              <template v-else>Sec. {{ section.title }}</template>
            </li>
          </ul>
        </li>
      </ul>
      <Button
        :text="buttonText"
        type="Link"
        size="Medium"
        @click="isExpanded = !isExpanded" />
    </div>
  </div>
</template>
<style lang="scss">
.CardPublishedIn {
  border: 1px solid $grey40;
  border-radius: spacing(1);
  padding: spacing(2) spacing(3);

  &__label {
    @include f("heading-sm-bold");
    color: $textPrimary;
    margin-bottom: spacing(3);
  }

  &__journal {
    @include f("heading-md-semibold");
    color: $textPrimary;
  }

  &__sections {
    margin-bottom: spacing(3);

    & + & {
      margin-top: spacing(0.5);
    }
  }

  &__section {
    @include f("heading-md");
    color: $textSecondary;
  }

  &__link {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
    }
  }

  &__metrics {
    display: flex;
    gap: spacing(2);
  }

  &__metric {
    @include f("label-md");
    color: $textTertiary;

    strong {
      font-weight: 700;
    }
  }

  &__otherJournals {
    margin-top: spacing(3);
  }

  &__count {
    @include f("heading-sm");
    color: $textPrimary;
    margin-bottom: spacing(0.5);
  }

  &__others {
    display: flex;
    flex-direction: column;
    padding-top: spacing(2);
  }
}
</style>
